import { useState } from 'react';
import { Check } from 'lucide-react';
import logoMark from '../../assets/images/logo-mark.svg';
import { CardSaveBadge, type CardSavePhase } from '../tenant/CardSaveBadge';
import { PersonAvatar } from '../tenant/PersonAvatar';
import ClosingDocumentSheet from './ClosingDocumentSheet';
import ClosingDocumentUpload, { ClosingSavedDocumentPreview } from './ClosingDocumentUpload';
import {
  housemateStepComplete,
  leaseStepComplete,
  SHOW_HOUSEMATE_AGREEMENT_STEP,
  utilitySetupStepComplete,
  type ClosingGroupState,
  type ClosingMember,
  type LeaseSigningSigner,
} from './closing-model';
import { CLOSING_BUTTON, CLOSING_CARD, CLOSING_DOCUMENT_PANEL } from './closing-styles';
import HousemateAgreementInstructions from './HousemateAgreementInstructions';
import UtilitiesKingstonSetupInstructions from './UtilitiesKingstonSetupInstructions';
import { ReferralCard } from '../tenant/ReferralCard';

const REFERRAL_FIELD_CLASS =
  'w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400';

export interface ClosingGroupReferralProps {
  referrerName: string;
  referrerSource: string;
  onReferrerNameChange: (value: string) => void;
  onReferrerSourceChange: (value: string) => void;
  savePhase?: CardSavePhase;
  nameId: string;
  sourceId: string;
}

interface ClosingGroupCardsProps {
  members: ClosingMember[];
  group: ClosingGroupState;
  utilityFile: File | null;
  housemateFile: File | null;
  onUtilityFile: (file: File) => void;
  onClearUtility: () => void;
  onDeleteUtility?: () => void;
  onHousemateFile: (file: File) => void;
  onClearHousemate: () => void;
  onDeleteHousemate?: () => void;
  utilitySavePhase?: CardSavePhase;
  housemateSavePhase?: CardSavePhase;
  getSavedDocumentUrl?: (documentId: string) => Promise<string>;
  onDownloadLease?: (documentId: string, fileName: string) => Promise<void>;
  referral?: ClosingGroupReferralProps;
}

const LEASE_STEPS = [
  'Receive your lease agreement by email',
  'Sign the agreement',
] as const;

function signerStruck(done: boolean): string {
  return done
    ? 'text-gray-400 line-through decoration-gray-400 dark:text-gray-500 dark:decoration-gray-500'
    : 'text-gray-900 dark:text-white';
}

function normalizedPersonName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function headshotForSigner(signer: LeaseSigningSigner, members: ClosingMember[]): string | null {
  if (signer.personId) {
    const byId = members.find((member) => member.id === signer.personId);
    if (byId) return byId.headshotDocumentId;
  }
  const name = normalizedPersonName(signer.name);
  if (!name) return null;
  const byName = members.find((member) => normalizedPersonName(member.displayName) === name);
  return byName?.headshotDocumentId ?? null;
}

function SignerName({
  signer,
  members,
  amberPeak = false,
}: {
  signer: LeaseSigningSigner;
  members: ClosingMember[];
  amberPeak?: boolean;
}) {
  const name = signer.name.trim() || (amberPeak ? 'Amber Peak' : 'Signer');
  return (
    <li className="flex items-center gap-2">
      <span className={signer.signed ? 'opacity-50' : undefined}>
        {amberPeak ? (
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-red-50 dark:bg-white/10">
            <img src={logoMark} alt="" className="h-4 w-4" />
          </span>
        ) : (
          <PersonAvatar documentId={headshotForSigner(signer, members)} onDark={false} size="sm" />
        )}
      </span>
      <span className={`min-w-0 text-sm font-medium leading-5 ${signerStruck(signer.signed)}`}>{name}</span>
    </li>
  );
}

function LeaseNumberedSteps({
  completedCount,
  signers,
  members,
}: {
  completedCount: number;
  signers: ClosingGroupState['leaseSigners'];
  members: ClosingMember[];
}) {
  const amberPeak = signers.filter((signer) => signer.role === 'amberPeak' || signer.name === 'Amber Peak');
  const tenants = signers.filter((signer) => !amberPeak.includes(signer));
  const tenantsDone = tenants.length > 0 && tenants.every((signer) => signer.signed);
  const amberPeakDone = amberPeak.length > 0 && amberPeak.every((signer) => signer.signed);

  return (
    <ol className="space-y-2.5">
      {LEASE_STEPS.map((label, index) => {
        const done = index < completedCount;
        const showSigners = index === 1 && (tenants.length > 0 || amberPeak.length > 0);
        return (
          <li key={label} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold leading-none text-white ${
                  done ? 'bg-gray-300 dark:bg-white/20' : 'bg-red-700'
                }`}
              >
                {index + 1}
              </span>
              <span className={`min-w-0 text-sm font-medium leading-5 ${signerStruck(done)}`}>
                {label}
              </span>
            </div>
            {showSigners ? (
              <div className="space-y-2.5 pl-10">
                {tenants.length > 0 ? (
                  <div>
                    <p className={`text-sm font-medium leading-5 ${signerStruck(tenantsDone)}`}>2.1 Tenant signatures</p>
                    <ul className="mt-1.5 space-y-1.5">
                      {tenants.map((signer, signerIndex) => (
                        <SignerName key={`${signer.personId ?? signer.name}-${signerIndex}`} signer={signer} members={members} />
                      ))}
                    </ul>
                  </div>
                ) : null}
                {amberPeak.length > 0 ? (
                  <div>
                    <p className={`text-sm font-medium leading-5 ${signerStruck(amberPeakDone)}`}>2.2 Manager signature</p>
                    <ul className="mt-1.5 space-y-1.5">
                      {amberPeak.map((signer, signerIndex) => (
                        <SignerName
                          key={`amber-peak-${signerIndex}`}
                          signer={signer}
                          members={members}
                          amberPeak
                        />
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function LeaseSigningProgress({
  members,
  group,
  getSavedDocumentUrl,
  onDownloadLease,
}: {
  members: ClosingMember[];
  group: ClosingGroupState;
  getSavedDocumentUrl?: (documentId: string) => Promise<string>;
  onDownloadLease?: (documentId: string, fileName: string) => Promise<void>;
}) {
  const signed = leaseStepComplete(members, group);
  const completedCount = !group.leaseSent ? 0 : signed ? LEASE_STEPS.length : 1;
  const hasDocument = Boolean(group.leaseDocumentId && group.leaseFileName && getSavedDocumentUrl);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function downloadLease() {
    if (!group.leaseDocumentId || !group.leaseFileName || !onDownloadLease || downloading) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      await onDownloadLease(group.leaseDocumentId, group.leaseFileName);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Unable to download the lease.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className={CLOSING_DOCUMENT_PANEL}>
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Steps to Complete</p>
          <div className="mt-3">
            <LeaseNumberedSteps
              completedCount={completedCount}
              signers={group.leaseSent ? group.leaseSigners : []}
              members={members}
            />
          </div>
        </div>
        <div className="flex w-full shrink-0 flex-col items-center gap-3 sm:w-52">
          <ClosingDocumentSheet filled={hasDocument}>
            {hasDocument && group.leaseDocumentId && group.leaseFileName && getSavedDocumentUrl ? (
              <ClosingSavedDocumentPreview
                documentId={group.leaseDocumentId}
                fileName={group.leaseFileName}
                getSavedDocumentUrl={getSavedDocumentUrl}
              />
            ) : (
              'Your lease agreement will appear here once signing is complete.'
            )}
          </ClosingDocumentSheet>
          {hasDocument && onDownloadLease ? (
            <button type="button" className={CLOSING_BUTTON} onClick={() => void downloadLease()} disabled={downloading}>
              {downloading ? 'Downloading…' : 'Download lease'}
            </button>
          ) : null}
          {downloadError ? (
            <p className="text-center text-xs text-rose-700 dark:text-rose-300">{downloadError}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CompleteMark({ done, label }: { done: boolean; label: string }) {
  if (!done) return null;
  return (
    <span className="absolute right-4 top-4 z-10 grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700 sm:right-5 sm:top-5" aria-label={`${label} complete`}>
      <Check size={16} strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

export default function ClosingGroupCards({
  members,
  group,
  utilityFile,
  housemateFile,
  onUtilityFile,
  onClearUtility,
  onDeleteUtility,
  onHousemateFile,
  onClearHousemate,
  onDeleteHousemate,
  utilitySavePhase = 'idle',
  housemateSavePhase = 'idle',
  getSavedDocumentUrl,
  onDownloadLease,
  referral,
}: ClosingGroupCardsProps) {
  const leaseDone = leaseStepComplete(members, group);
  const utilitiesDone = utilitySetupStepComplete(group);
  const housemateDone = housemateStepComplete(group);

  const gridClass = SHOW_HOUSEMATE_AGREEMENT_STEP
    ? 'sm:grid-cols-2 lg:grid-cols-3'
    : 'sm:grid-cols-2 lg:grid-cols-2';

  return (
    <div className={`grid grid-cols-1 items-stretch gap-4 ${gridClass}`}>
      <section className={`${CLOSING_CARD} flex h-full flex-col sm:col-span-2 lg:col-span-1`}>
        <CompleteMark done={leaseDone} label="Sign the Lease" />
        <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Sign the Lease</h2>
        <LeaseSigningProgress
          members={members}
          group={group}
          getSavedDocumentUrl={getSavedDocumentUrl}
          onDownloadLease={onDownloadLease}
        />
      </section>

      <section className={`${CLOSING_CARD} flex h-full flex-col sm:col-span-1`}>
        <CardSaveBadge phase={utilitySavePhase} complete={utilitiesDone} />
        <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Utilities</h2>
        <ClosingDocumentUpload
          note=""
          instructionsTone="utilities"
          instructionsExtra={<UtilitiesKingstonSetupInstructions context={group.utilitiesKingston} />}
          emptyPreview="Your utility confirmation will appear here when complete."
          previewSheet
          fileName={group.utilityFileName}
          savedDocumentId={group.utilityDocumentId}
          getSavedDocumentUrl={getSavedDocumentUrl}
          file={utilityFile}
          onFile={onUtilityFile}
          onClear={onClearUtility}
          onDeleteSaved={onDeleteUtility}
          disabled={utilitySavePhase === 'saving'}
          reviewStatus={{
            review: group.utilitySetupReview,
            hasSubmission: Boolean(group.utilityFileName),
          }}
        />
      </section>

      {referral ? (
        <ReferralCard
          className={`${CLOSING_CARD} flex h-full min-h-0 w-full flex-col sm:col-span-2 lg:col-span-1`}
          titleClassName="pr-8 text-lg font-bold text-gray-900 dark:text-white"
          fieldClass={REFERRAL_FIELD_CLASS}
          savePhase={referral.savePhase}
          compactColumn
          limitFieldWidth
          optional
          referrerName={referral.referrerName}
          referrerSource={referral.referrerSource}
          onReferrerNameChange={referral.onReferrerNameChange}
          onReferrerSourceChange={referral.onReferrerSourceChange}
          nameId={referral.nameId}
          sourceId={referral.sourceId}
        />
      ) : null}

      {SHOW_HOUSEMATE_AGREEMENT_STEP ? (
        <section className={`${CLOSING_CARD} flex h-full flex-col sm:col-span-1 lg:col-span-1`}>
          <CardSaveBadge phase={housemateSavePhase} complete={housemateDone} />
          <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Housemate agreement</h2>
          <ClosingDocumentUpload
            note=""
            instructionsExtra={<HousemateAgreementInstructions templateReady={group.housemateTemplateReady} />}
            fileName={group.housemateSignedFileName}
            savedDocumentId={group.housemateDocumentId}
            getSavedDocumentUrl={getSavedDocumentUrl}
            file={housemateFile}
            onFile={onHousemateFile}
            onClear={onClearHousemate}
            onDeleteSaved={onDeleteHousemate}
            disabled={housemateSavePhase === 'saving'}
            reviewStatus={{
              review: group.housemateAgreementReview,
              hasSubmission: Boolean(group.housemateSignedFileName),
            }}
          />
        </section>
      ) : null}
    </div>
  );
}
